import React, { useEffect, useState } from "react";
import {
  Box,
  MenuItem,
  Select,
  Checkbox,
  ListItemText,
  InputLabel,
  FormControl,
  OutlinedInput,
  SelectChangeEvent,
  TextareaAutosize,
  Typography,
  useTheme,
  Grid2,
} from "@mui/material";
import { useStores } from "../../../use-stores";
import { DeliveryPartner } from "../../../../../models/Shipments";
import { ShipmentType } from "../../../../../models/Shipments/ShipmentStatus";
import AppSearchDropDown2 from "../../../../../views/components/common/filter/AppSearchDropdown2";
import AppSearchDropDown from "../../../../../views/components/common/filter/AppSearchDropDown";
import AppSearchDropdownMulti from "../../../../../views/components/common/filter/AppSearchDropdownMulti";
import { FilterValue } from "../../../../../models/Filters/FilterValue";
import { observer } from "mobx-react";
import { InventoryTypeOptions } from "../../../../../models/inventory/InventoryListing";
import { InventoryVariant } from "../../../../../models/inventory/InventoryAdmin";
import AppDropDown from "../../../../../views/components/common/filter/AppDropDown";
import FormSmallTextField from "../../../../../views/components/common/form/FormTextField";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import CustomTextField from "../../../../../views/components/common/textFields/TextField";
import { DateRangePickerNew } from "../../../../../views/components/common/textFields/DatePickerRangeNew";
import InventoryAPI from "../../../api/InventoryAPI";
import dayjs, { Dayjs } from "dayjs";

const inventoryAPI = new InventoryAPI();

const useStyles = {
  filterContainer: {
    marginTop: "0.5rem",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    width: "100%",
    "@media (max-width: 990px)": {
      flexDirection: "column",
    },
  },
  rowContainer: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "end",
    gap: "10px",
    alignItems: "center",
    "@media (max-width: 990px)": {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  filterItem: {
    minWidth: "180px",
    flex: "1 1 auto",
    maxWidth: "220px",
  },
  buttonContainer: {
    display: "flex",
    gap: "1rem",
    alignItems: "center",
  },
};

const DimensionsFilter = [
  { id: 1, value: "Dimensions" },
  { id: 2, value: "Weight" },
  { id: 3, value: "Shipping Cost" },
  { id: 4, value: "AWB" },
  { id: 5, value: "Packs" },
];

const MAX_CLIENT_SKU_FILTER = 20;

const parseClientSkuParam = (clientSkuParam: string | null): FilterValue[] => {
  if (!clientSkuParam) return [];

  try {
    const parsed = JSON.parse(clientSkuParam);
    if (Array.isArray(parsed)) {
      return parsed
        .map((item) => {
          if (typeof item === "string") {
            return { id: item, value: item };
          }
          const sku = String(item?.value || item?.id || "").trim();
          if (!sku) return null;
          return {
            id: sku,
            value: sku,
            subtitle: item?.subtitle,
          };
        })
        .filter((item): item is FilterValue => Boolean(item))
        .slice(0, MAX_CLIENT_SKU_FILTER);
    }
  } catch {
    // Fall through to comma-separated SKUs
  }

  return clientSkuParam
    .split(",")
    .map((sku) => sku.trim())
    .filter(Boolean)
    .slice(0, MAX_CLIENT_SKU_FILTER)
    .map((sku) => ({ id: sku, value: sku }));
};

interface FilterComponentProps {
  onFilterChange: (filters: {
    shipmentType: number[];
    deliveryPartner: number[];
    dimensions: number[];
    orderIds: number[];
    inventoryType: string;
    emails?: string;
    clientSkus: FilterValue[];
    packs_exist?: boolean | undefined;
    country?: string[] | undefined;
    search_text_value?: (number | string)[] | undefined;
    contact_number?: string | undefined;
    customers: FilterValue[];
    start_date: Dayjs | null;
    end_date: Dayjs | null;
  }) => void;
  deliveryPartners: DeliveryPartner[];
  lockedOrderId?: number;
  lockedInventoryType?: string;
  simpleSearchOnly?: boolean;
}

const FilterComponent: React.FC<FilterComponentProps> = ({
  onFilterChange,
  deliveryPartners,
  lockedOrderId,
  lockedInventoryType,
  simpleSearchOnly = false,
}) => {
  const [selectedFilters, setSelectedFilters] = useState({
    contact_number: undefined as string | undefined,
    shipmentType: [] as number[],
    deliveryPartner: [] as number[],
    orderIds: [] as number[],
    dimensions: [] as number[],
    inventoryType: "-1",
    start_date: null as Dayjs | null,
    end_date: null as Dayjs | null,
    emails: "",
    clientSkus: [] as FilterValue[],
    packs_exist: undefined as boolean | undefined,
    country: [] as string[] | undefined,
    search_text_value: [] as (number | string)[] | undefined,
    customers: [] as FilterValue[],
  });

  const theme = useTheme();
  const [searchedVariants, setSearchedVariants] = useState<InventoryVariant[]>(
    [],
  );
  const [packsSearchError, setPacksSearchError] = useState("");

  // const [orderOptions, setOrderOptions] = useState<any[]>([]);
  // const [loading, setLoading] = useState(false);
  // const [searchQuery, setSearchQuery] = useState("");
  // const [dropdownOpen, setDropdownOpen] = useState(false);
  let apiTimer: any;

  const { shipmentStore, customerStore } = useStores();

  const buildSimpleSearchFilters = (
    search_text_value: (number | string)[] | undefined,
  ) => ({
    contact_number: undefined as string | undefined,
    shipmentType: [] as number[],
    deliveryPartner: [] as number[],
    orderIds: lockedOrderId ? [lockedOrderId] : [],
    dimensions: [] as number[],
    inventoryType: lockedInventoryType || "-1",
    emails: "",
    clientSkus: [] as FilterValue[],
    packs_exist: undefined as boolean | undefined,
    country: [] as string[] | undefined,
    search_text_value,
    customers: [] as FilterValue[],
    start_date: null as Dayjs | null,
    end_date: null as Dayjs | null,
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    const search_text_value = params.get("search_text_value")
      ? params
          .get("search_text_value")!
          .split(",")
          .map((item) => {
            const trimmed = item.trim();
            const num = Number(trimmed);
            return !isNaN(num) && num !== 0 ? num : trimmed;
          })
      : [];

    if (simpleSearchOnly) {
      setSelectedFilters((prev) => ({
        ...prev,
        ...buildSimpleSearchFilters(search_text_value),
      }));
      return;
    }

    const clientSkus = parseClientSkuParam(params.get("clientSku"));

    let customers: FilterValue[] = [];
    const customersParam = params.get("c_id");
    if (customersParam) {
      customers = customersParam.split(",").map((item) => {
        const list = item.split(":")!;
        return { id: list[0], value: list[1] };
      });
    }

    setSelectedFilters((prev) => ({
      ...prev,
      shipmentType: (params.get("shipment_type")?.split(",").map(Number) ||
        []) as number[],
      deliveryPartner: (params
        .get("delivery_partner_id")
        ?.split(",")
        .map(Number) || []) as number[],
      orderIds: lockedOrderId
        ? [lockedOrderId]
        : ((params.get("order_id")?.split(",").map(Number) || []) as number[]),
      dimensions: (params.get("dimensions")?.split(",").map(Number) ||
        []) as number[],
      inventoryType:
        lockedInventoryType || params.get("inventory_type") || "-1",
      clientSkus,
      packs_exist:
        params.get("packs_exist") === "true"
          ? true
          : params.get("packs_exist") === "false"
            ? false
            : undefined,
      country:
        params
          .get("country")
          ?.split(",")
          .filter((item) => item.trim()) || [],
      search_text_value: params.get("search_text_value")
        ? params
            .get("search_text_value")!
            .split(",")
            .map((item) => {
              const trimmed = item.trim();
              const num = Number(trimmed);
              return !isNaN(num) && num !== 0 ? num : trimmed;
            })
        : [],
      contact_number: params.get("contact_number") || undefined,
      customers,
      start_date: params.get("start_date")
        ? dayjs(params.get("start_date"), "YYYY-MM-DD")
        : null,
      end_date: params.get("end_date")
        ? dayjs(params.get("end_date"), "YYYY-MM-DD")
        : null,
    }));
  }, [shipmentStore, lockedOrderId, lockedInventoryType, simpleSearchOnly]);

  const handleSelectionChange =
    (field: keyof typeof selectedFilters) =>
    (event: SelectChangeEvent<any[]>) => {
      const selectedValues = event.target.value as any[];

      setSelectedFilters((prev) => {
        const newFilters = {
          ...prev,
          [field]: selectedValues,
        };

        // If dimensions filter includes 5, set packs_exist to false
        if (field === "dimensions" && selectedValues?.includes(5)) {
          newFilters.packs_exist = undefined;
        }

        return newFilters;
      });
    };

  const handlePacksExistChange = (value: string) => {
    setSelectedFilters((prev) => ({
      ...prev,
      packs_exist:
        value === "true" ? true : value === "false" ? false : undefined,
    }));
  };

  const handleCountryChange = (value: string) => {
    const val = value;
    setSelectedFilters((prev) => ({
      ...prev,
      country: val
        .split(",")
        .map((item) => item.trim())
        .filter((item) => item.length > 0),
    }));
  };

  const handleShipmentIdChange = (value: string) => {
    const trimmedValue = value.trim();
    const startsWithNumber =
      trimmedValue.length > 0 && /^\d/.test(trimmedValue);

    let processedValue = value;

    if (startsWithNumber) {
      processedValue = processedValue.replace(/\s/g, "");
    }

    const isEmpty = processedValue.trim() === "";

    if (isEmpty) {
      setSelectedFilters((prev) => {
        const updatedFilters = {
          ...prev,
          search_text_value: [],
        };
        setTimeout(() => {
          onFilterChange(
            simpleSearchOnly
              ? buildSimpleSearchFilters([])
              : updatedFilters,
          );
        }, 0);
        return updatedFilters;
      });
      return;
    }

    if (startsWithNumber) {
      const parts = processedValue.split(",");
      const newShipmentId: (number | string)[] = parts
        .map((item) => {
          const trimmed = item.trim();
          if (trimmed === "") return null;
          const num = Number(trimmed);
          return !isNaN(num) && num !== 0 ? num : trimmed;
        })
        .filter((item) => item !== null) as (number | string)[];

      setSelectedFilters((prev) => {
        const updatedFilters = {
          ...prev,
          search_text_value: newShipmentId,
        };
        return updatedFilters;
      });
    } else {
      setSelectedFilters((prev) => {
        const updatedFilters = {
          ...prev,
          search_text_value: [processedValue],
        };
        return updatedFilters;
      });
    }
  };

  const handleContactNumberChange = (value: string) => {
    const trimmedValue = value.trim();
    const startsWithNumber =
      trimmedValue.length > 0 && /^\d/.test(trimmedValue);

    let processedValue = value;

    if (startsWithNumber) {
      processedValue = processedValue.replace(/\s/g, "");
    }

    const isEmpty = processedValue.trim() === "";

    if (isEmpty) {
      setSelectedFilters((prev) => {
        const updatedFilters = {
          ...prev,
          contact_number: undefined,
        };
        setTimeout(() => {
          onFilterChange(updatedFilters);
        }, 0);
        return updatedFilters;
      });
      return;
    }

    if (startsWithNumber) {
      setSelectedFilters((prev) => {
        const updatedFilters = {
          ...prev,
          contact_number: processedValue,
        };
        setTimeout(() => {
          onFilterChange(updatedFilters);
        }, 0);
        return updatedFilters;
      });
    }
  };

  const handleClear = () => {
    setPacksSearchError("");
    setSearchedVariants([]);
    setSelectedFilters({
      contact_number: undefined,
      shipmentType: [],
      deliveryPartner: [],
      orderIds: lockedOrderId ? [lockedOrderId] : [],
      dimensions: [],
      inventoryType: lockedInventoryType || "-1",
      emails: "",
      clientSkus: [] as FilterValue[],
      packs_exist: undefined,
      country: [] as string[] | undefined,
      search_text_value: [] as (number | string)[] | undefined,
      customers: [] as FilterValue[],
      start_date: null as Dayjs | null,
      end_date: null as Dayjs | null,
    });
    onFilterChange({
      contact_number: undefined,
      shipmentType: [],
      deliveryPartner: [],
      orderIds: lockedOrderId ? [lockedOrderId] : [],
      dimensions: [],
      inventoryType: lockedInventoryType || "-1",
      emails: "",
      clientSkus: [] as FilterValue[],
      packs_exist: undefined,
      country: [] as string[] | undefined,
      search_text_value: [] as (number | string)[] | undefined,
      customers: [] as FilterValue[],
      start_date: null as Dayjs | null,
      end_date: null as Dayjs | null,
    });
  };

  // const handleSearchChange = (event: any) => {
  //   setSearchQuery(event.target.value);
  // };

  const handleApply = () => {
    const trimmedSearchText =
      selectedFilters.search_text_value?.map((item) => {
        if (typeof item === "string") {
          return item.trimEnd();
        }
        return item;
      }) || [];

    if (simpleSearchOnly) {
      onFilterChange(buildSimpleSearchFilters(trimmedSearchText));
      return;
    }

    onFilterChange({
      ...selectedFilters,
      search_text_value: trimmedSearchText,
      orderIds: lockedOrderId ? [lockedOrderId] : selectedFilters.orderIds,
      inventoryType: lockedInventoryType || selectedFilters.inventoryType,
    });
  };

  // useEffect(() => {
  //   const fetchOrderOptions = async () => {
  //     if (searchQuery.length > 1 && dropdownOpen) {
  //       setLoading(true);
  //       const data = await shipmentStore.getOrder(searchQuery);
  //       setOrderOptions(data || []);
  //       setLoading(false);
  //     }
  //   };

  //   const handler = setTimeout(() => {
  //     fetchOrderOptions();
  //     setOrderOptions([]);
  //   }, 1000);

  //   return () => {
  //     clearTimeout(handler);
  //   };
  // }, [searchQuery, dropdownOpen, shipmentStore]);

  // const handleDropdownOpen = () => {
  //   setSearchQuery("");
  //   setOrderOptions([]);
  //   setDropdownOpen(true);
  // };

  // const handleDropdownClose = () => {
  //   setDropdownOpen(false);
  // };

  const handlePacksSearch = (searchText?: string) => {
    if (apiTimer) clearTimeout(apiTimer);
    if (searchText && searchText.length > 2)
      apiTimer = setTimeout(async () => {
        try {
          setPacksSearchError("");
          const response = await inventoryAPI.getVariants({
            search: searchText,
          });
          setSearchedVariants(response?.variants ?? []);
        } catch (error: any) {
          setSearchedVariants([]);
          setPacksSearchError(
            error?.response?.data?.message ||
              error?.message ||
              "Failed to search packs",
          );
        }
      }, 1500);
  };

  const handleOrderSearch = (searchText?: string) => {
    if (apiTimer) clearTimeout(apiTimer);
    if (searchText && searchText.length > 2)
      apiTimer = setTimeout(() => {
        shipmentStore.getOrder(searchText);
      }, 1500);
  };

  const handleCustomerSearch = (searchText?: string) => {
    if (apiTimer) clearTimeout(apiTimer);
    if (searchText && searchText.length > 2)
      apiTimer = setTimeout(() => {
        customerStore
          .searchCustomers({ page_no: 1, search_text: searchText })
          .catch(() => {});
      }, 1500);
  };

  if (simpleSearchOnly) {
    return (
      <Grid2
        container
        spacing={2}
        alignItems="center"
        sx={{ mb: 2 }}
        display="flex"
        justifyContent="space-between"
      >
        <Grid2 size={{ xs: 12, md: 9 }}>
          <CustomTextField
            value={selectedFilters.search_text_value?.join(", ") || ""}
            width="100%"
            placeholder="Search Shipment ID or Name"
            customStyles={{
              backgroundColor:
                theme?.palette?.customColors?.grayBackGroundColor,
            }}
            onChange={(event) =>
              handleShipmentIdChange(event.target.value || "")
            }
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                handleApply();
                return;
              }

              if (event.key === " ") {
                const inputElement = event.target as HTMLInputElement;
                const currentValue = inputElement.value || "";
                const trimmedValue = currentValue.trim();
                const firstChar = trimmedValue[0];

                if (firstChar && /^\d/.test(firstChar)) {
                  event.preventDefault();
                }
              }
            }}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 3 }}>
          <Box
            display="flex"
            flexDirection="row"
            gap={2}
            justifyContent={{ xs: "flex-start", md: "flex-end" }}
            alignItems="center"
          >
            <AppButton
              variant="primary"
              label="Apply"
              onClick={handleApply}
              customStyles={{ minWidth: 120, height: "38px" }}
            />
            <AppButton
              variant="transparentPrimary"
              label="Clear"
              onClick={handleClear}
              customStyles={{ minWidth: 120, height: "38px" }}
            />
          </Box>
        </Grid2>
      </Grid2>
    );
  }

  return (
    <>
      <Box sx={useStyles.filterContainer}>
        <Grid2 container spacing={2} columns={12}>
          {!simpleSearchOnly && (
            <>
          <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
            <Box
              sx={{
                display: "flex",
              }}
            >
              <TextareaAutosize
                placeholder="Copy paste comma separated email ids here. Limit to 200 ids."
                onChange={(event) => {
                  setSelectedFilters((prev) => ({
                    ...prev,
                    emails: event.target.value.replace(/ /g, ""),
                  }));
                }}
                value={selectedFilters.emails}
                style={{
                  width: "100%",
                  borderColor: "#D3D3D3",
                  borderRadius: 4,
                  padding: 6,
                  fontSize: "12px",
                  minHeight: "28px",
                  maxHeight: "48px",
                }}
              ></TextareaAutosize>
            </Box>
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 12, md: 3, lg: 3 }}>
            <DateRangePickerNew
              placeholder="Created Date"
              onRangeSelect={(range) => {
                setSelectedFilters((prev) => ({
                  ...prev,
                  start_date: range.startDate,
                  end_date: range.endDate,
                }));
              }}
              startDate={selectedFilters.start_date}
              endDate={selectedFilters.end_date}
              zIndex={3001}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 12, md: 3, lg: 3 }}>
            <FormSmallTextField
              placeholder="Search By Contact Number"
              fontSize="12px"
              variant="outlined"
              type="text"
              label="Search By Contact Number"
              value={selectedFilters.contact_number || ""}
              onChange={(id, value) => handleContactNumberChange(value || "")}
              borderColor={`1px solid ${theme.palette.customColors.greyBorder}`}
              sx={{
                marginBottom: "0px",
              }}
            />
          </Grid2>
            </>
          )}
          <Grid2
            size={{
              xs: 12,
              sm: 12,
              md: simpleSearchOnly ? 6 : 3,
              lg: simpleSearchOnly ? 6 : 3,
            }}
          >
            <FormSmallTextField
              placeholder="Search Shipment ID or Name"
              fontSize="12px"
              variant="outlined"
              type="text"
              label="Search Shipment ID or Name"
              value={selectedFilters.search_text_value?.join(", ") || ""}
              onChange={(id, value) => handleShipmentIdChange(value || "")}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  handleApply();
                  return;
                }

                if (event.key === " ") {
                  const inputElement = event.target as HTMLInputElement;
                  const currentValue = inputElement.value || "";

                  const trimmedValue = currentValue.trim();
                  const firstChar = trimmedValue[0];

                  if (firstChar && /^\d/.test(firstChar)) {
                    event.preventDefault();
                  }
                }
              }}
              borderColor={`1px solid ${theme.palette.customColors.greyBorder}`}
              sx={{
                marginBottom: "0px",
              }}
            />
          </Grid2>
          {!simpleSearchOnly && (
            <>
          {!lockedOrderId && (
          <Grid2 size={{ xs: 12, sm: 6, md: 3, lg: 3 }}>
            <FormControl fullWidth>
              <Select
                value={selectedFilters.inventoryType}
                onChange={(event) =>
                  setSelectedFilters((prev) => ({
                    ...prev,
                    orderIds: event.target.value === "1" ? [] : prev.orderIds,
                    inventoryType: event.target.value,
                  }))
                }
                // input={
                //   <OutlinedInput
                //     label="Inventory Type"
                //     sx={{ height: "40px" }}
                //   />
                // }
                renderValue={(selected) =>
                  selected ? (
                    <Typography sx={{ fontSize: "0.8rem" }}>
                      {
                        InventoryTypeOptions.find(
                          (option) => option.id === selected,
                        )?.value
                      }
                    </Typography>
                  ) : (
                    <Typography sx={{ fontSize: "0.8rem" }}>Select</Typography>
                  )
                }
                MenuProps={{
                  PaperProps: { style: { maxHeight: 200, overflowY: "auto" } },
                }}
                sx={{ height: "40px" }}
              >
                {InventoryTypeOptions.map((option) => (
                  <MenuItem
                    key={option.id}
                    value={option.id}
                    sx={{ height: "30px", fontSize: "0.8rem" }}
                  >
                    {option.value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid2>
          )}

          <Grid2 size={{ xs: 12, sm: 6, md: 3, lg: 3 }}>
            <FormControl fullWidth>
              <InputLabel sx={{ top: "-7px", fontSize: "12px" }}>
                Shipment Type
              </InputLabel>
              <Select
                multiple
                value={selectedFilters.shipmentType}
                onChange={handleSelectionChange("shipmentType")}
                input={
                  <OutlinedInput
                    label="Shipment Type"
                    sx={{ height: "40px" }}
                  />
                }
                renderValue={(selected) => `${selected.length} selected`}
                MenuProps={{
                  PaperProps: { style: { maxHeight: 200, overflowY: "auto" } },
                }}
                sx={{ height: "35px", padding: "0px" }}
              >
                {ShipmentType.map((option) => (
                  <MenuItem
                    key={option.id}
                    value={option.id}
                    sx={{ height: "30px" }}
                  >
                    <Checkbox
                      checked={
                        selectedFilters.shipmentType.indexOf(option.id) > -1
                      }
                      sx={{ padding: "0 8px" }}
                    />
                    <ListItemText primary={option.value} sx={{ margin: 0 }} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 6, md: 3, lg: 3 }}>
            <FormControl fullWidth>
              <InputLabel sx={{ top: "-7px", fontSize: "12px" }}>
                Delivery Partner
              </InputLabel>
              <Select
                multiple
                value={selectedFilters.deliveryPartner}
                onChange={handleSelectionChange("deliveryPartner")}
                input={
                  <OutlinedInput
                    label="Delivery Partner"
                    sx={{ height: "40px" }}
                  />
                }
                renderValue={(selected) => `${selected.length} selected`}
                MenuProps={{
                  PaperProps: { style: { maxHeight: 200, overflowY: "auto" } },
                }}
                sx={{ height: "35px", padding: "0px" }}
              >
                {deliveryPartners?.map((partner) => (
                  <MenuItem
                    key={partner.delivery_partner_id}
                    value={partner.delivery_partner_id}
                    sx={{ height: "30px" }}
                  >
                    <Checkbox
                      checked={
                        selectedFilters.deliveryPartner.indexOf(
                          partner.delivery_partner_id,
                        ) > -1
                      }
                      sx={{ padding: "0 8px" }}
                    />
                    <ListItemText
                      primary={partner.delivery_partner_name}
                      sx={{ margin: 0 }}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 6, md: 3, lg: 3 }}>
            <AppSearchDropDown
              isMultiSelect={false}
              title="Select Customers"
              value={selectedFilters.customers}
              fontSize="12px"
              list={customerStore.customers.map((item) => {
                return { id: String(item.id), value: item.name! };
              })}
              local={false}
              handleSearch={handleCustomerSearch}
              handleSelect={(items: FilterValue[]) => {
                setSelectedFilters((prev) => ({
                  ...prev,
                  customers: items,
                }));
              }}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 6, md: 3, lg: 3 }}>
            <FormControl fullWidth>
              <InputLabel sx={{ top: "-7px", fontSize: "12px" }}>
                Missing values
              </InputLabel>
              <Select
                multiple
                value={selectedFilters.dimensions}
                onChange={handleSelectionChange("dimensions")}
                input={
                  <OutlinedInput label="Dimensions" sx={{ height: "40px" }} />
                }
                renderValue={(selected) => `${selected.length} selected`}
                MenuProps={{
                  PaperProps: { style: { maxHeight: 200, overflowY: "auto" } },
                }}
                sx={{ height: "35px", padding: "0px" }}
              >
                {DimensionsFilter.map((dimension) => (
                  <MenuItem
                    key={dimension.id}
                    value={dimension.id}
                    sx={{ height: "30px" }}
                  >
                    <Checkbox
                      checked={
                        selectedFilters.dimensions.indexOf(dimension.id) > -1
                      }
                      sx={{ padding: "0 8px" }}
                    />
                    <ListItemText
                      primary={dimension.value}
                      sx={{ margin: 0 }}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 6, md: 3, lg: 3 }}>
            <AppDropDown
              title="Packs Exist"
              fontSize="12px"
              value={
                selectedFilters.packs_exist === undefined
                  ? "-1"
                  : String(selectedFilters.packs_exist)
              }
              isMultiSelect={false}
              onSingleSelectionChange={(id) => handlePacksExistChange(id)}
              list={[
                { id: "-1", value: "Select" },
                { id: "true", value: "Yes" },
                { id: "false", value: "No" },
              ]}
              borderColor={`1px solid ${theme.palette.customColors.greyBorder}`}
              borderRadius="4px"
              disabled={selectedFilters?.dimensions?.includes(5)}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 6, md: 3, lg: 3 }}>
            <FormSmallTextField
              placeholder="Enter country"
              fontSize="12px"
              variant="outlined"
              label="Country"
              type="text"
              value={selectedFilters.country?.join(", ")}
              onChange={(id, value) => handleCountryChange(value || "")}
              borderColor={`1px solid ${theme.palette.customColors.greyBorder}`}
              sx={{
                marginBottom: "0px",
              }}
            />
          </Grid2>

          {!lockedOrderId && (
          <Grid2 size={{ xs: 12, sm: 6, md: 3, lg: 3 }}>
            <AppSearchDropDown2
              minWidth="180px"
              isMultiSelect={true}
              title="Search Order"
              value={selectedFilters.orderIds.map((id) => ({
                id: String(id),
                value: String(id),
              }))}
              fontSize="12px"
              list={shipmentStore.orderIds.map((item) => ({
                id: String(item.id),
                value: item.value!,
              }))}
              local={false}
              handleSearch={handleOrderSearch}
              handleSelect={(items: FilterValue[]) => {
                const orderIds = items.map((item) => Number(item.id));
                setSelectedFilters((prev) => ({
                  ...prev,
                  orderIds,
                }));
              }}
              disabled={selectedFilters.inventoryType === "1"}
            />
          </Grid2>
          )}

          <Grid2 size={{ xs: 12, sm: 6, md: 3, lg: 3 }}>
            <AppSearchDropdownMulti
              minWidth="180px"
              isMultiSelect={true}
              title="Search by client SKU"
              value={selectedFilters.clientSkus}
              fontSize="12px"
              list={searchedVariants.map((variant) => ({
                id: variant.clientSku,
                value: variant.clientSku,
                subtitle: variant.productName,
              }))}
              local={false}
              handleSearch={handlePacksSearch}
              handleSelect={(items: FilterValue[]) => {
                const limitedItems = items.slice(0, MAX_CLIENT_SKU_FILTER);
                setPacksSearchError(
                  items.length > MAX_CLIENT_SKU_FILTER
                    ? `You can select up to ${MAX_CLIENT_SKU_FILTER} SKUs`
                    : "",
                );
                setSelectedFilters((prev) => ({
                  ...prev,
                  clientSkus: limitedItems,
                }));
              }}
              error={Boolean(packsSearchError)}
              errorMessage={packsSearchError}
              borderColor={theme.palette.customColors.greyBorder}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6, md: 3, lg: 3 }}></Grid2>
          <Grid2 size={{ xs: 12, sm: 6, md: 3, lg: 3 }}></Grid2>
          <Grid2 size={{ xs: 12, sm: 6, md: 3, lg: 3 }}></Grid2>
            </>
          )}
          <Grid2
            size={{
              xs: 12,
              sm: 6,
              md: simpleSearchOnly ? 6 : 3,
              lg: simpleSearchOnly ? 6 : 3,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                flexWrap: "wrap",
                gap: 1.5,
              }}
            >
              <AppButton
                variant="primary"
                label="Apply"
                onClick={handleApply}
                customStyles={{ minWidth: 120 }}
              />
              <AppButton
                variant="transparentPrimary"
                label="Clear"
                onClick={handleClear}
                customStyles={{ minWidth: 120 }}
              />
            </Box>
          </Grid2>
        </Grid2>
      </Box>
    </>
  );
};

export default observer(FilterComponent);
